import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  CreateTechRadarEntryInput,
  CreateTechRadarTimelineInput,
  TechRadarEntriesResponse,
  TechRadarEntry,
  TechRadarTimelineEntry,
  TechRadarTimelineResponse,
  UpdateTechRadarEntryInput,
  UpdateTechRadarTimelineInput,
} from '@internal/backstage-plugin-tech-radar-common';
import type { TechRadarApi } from './TechRadarApi';

type TechRadarClientOptions = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
};

export class TechRadarClient implements TechRadarApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: TechRadarClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async listEntries(): Promise<TechRadarEntry[]> {
    const response = await this.fetchJson<TechRadarEntriesResponse>('/entries');
    return response.entries;
  }

  async getEntry(id: string): Promise<TechRadarEntry> {
    return this.fetchJson<TechRadarEntry>(`/entries/${encodeURIComponent(id)}`);
  }

  async createEntry(input: CreateTechRadarEntryInput): Promise<TechRadarEntry> {
    return this.fetchJson<TechRadarEntry>('/entries', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateEntry(
    id: string,
    update: UpdateTechRadarEntryInput,
  ): Promise<TechRadarEntry> {
    return this.fetchJson<TechRadarEntry>(`/entries/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
  }

  async deleteEntry(id: string): Promise<void> {
    await this.fetchJson<void>(`/entries/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async listTimeline(entryId: string): Promise<TechRadarTimelineEntry[]> {
    const response = await this.fetchJson<TechRadarTimelineResponse>(
      `/entries/${encodeURIComponent(entryId)}/timeline`,
    );
    return response.items;
  }

  async getTimeline(
    entryId: string,
    timelineId: string,
  ): Promise<TechRadarTimelineEntry> {
    return this.fetchJson<TechRadarTimelineEntry>(
      `/entries/${encodeURIComponent(entryId)}/timeline/${encodeURIComponent(
        timelineId,
      )}`,
    );
  }

  async createTimeline(
    entryId: string,
    input: CreateTechRadarTimelineInput,
  ): Promise<TechRadarTimelineEntry> {
    return this.fetchJson<TechRadarTimelineEntry>(
      `/entries/${encodeURIComponent(entryId)}/timeline`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  }

  async updateTimeline(
    entryId: string,
    timelineId: string,
    update: UpdateTechRadarTimelineInput,
  ): Promise<TechRadarTimelineEntry> {
    return this.fetchJson<TechRadarTimelineEntry>(
      `/entries/${encodeURIComponent(entryId)}/timeline/${encodeURIComponent(
        timelineId,
      )}`,
      {
        method: 'PATCH',
        body: JSON.stringify(update),
      },
    );
  }

  async deleteTimeline(entryId: string, timelineId: string): Promise<void> {
    await this.fetchJson<void>(
      `/entries/${encodeURIComponent(entryId)}/timeline/${encodeURIComponent(
        timelineId,
      )}`,
      {
        method: 'DELETE',
      },
    );
  }

  private async fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}
